import { FC, useCallback, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { CustomTooltip } from 'src/components/tooltip';
import { Button } from 'src/components/button';
import { ControlledField } from 'src/components/controlled-field';
import { useUpdateBasket } from 'src/lib/hooks/use-patch-update-basket';
import { getTheme } from 'src/utils/getTheme';

import { ApiBasket } from '../../api/basketService';
import { ErrorSnackbarContext } from '../error-snackbar/error-snackbar-context';
import { ErrorSnackbarActionType } from '../error-snackbar/types';

import { ReferralCodeFormValues } from './types';
import { styles } from './styles';
import { referralCodeSchema } from './referral-code-schema';
import { referralCodeDataKeys } from './constants';

const { isHosTheme } = getTheme();

export const ReferralCodeCard: FC<{ basket: ApiBasket }> = ({ basket }) => {
    const { t } = useTranslation();
    const { mutate: updateBasket, isPending } = useUpdateBasket();
    const dispatchSnackbarError = useContext(ErrorSnackbarContext);

    const { control, watch, setValue, handleSubmit, formState } = useForm<ReferralCodeFormValues>({
        resolver: yupResolver(referralCodeSchema) as Resolver<ReferralCodeFormValues>,
        defaultValues: { referralCode: basket?.options.referrerCode || '' },
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const referralCode = watch(referralCodeDataKeys.referralCode);
    const isDisabled = referralCode === '' || formState.isValidating || isPending;

    const onValid = useCallback(
        async ({ referralCode: code }: ReferralCodeFormValues) => {
            updateBasket(
                {
                    basketId: basket.id,
                    basketData: {
                        ...basket,
                        options: {
                            ...basket.options,
                            referrerCode: code || null,
                        },
                    },
                    updatedAt: basket.updated_date,
                },
                {
                    onError: () => {
                        const errorType = isHosTheme
                            ? ErrorSnackbarActionType.ERROR_APPLY_REFERRAL_CODE_HOS
                            : ErrorSnackbarActionType.ERROR_APPLY_REFERRAL_CODE;

                        dispatchSnackbarError({ type: errorType, payload: null });
                    },
                }
            );
        },
        [basket, updateBasket, t]
    );

    const handleClearReferralCode = useCallback(async () => {
        updateBasket(
            {
                basketId: basket.id,
                basketData: {
                    ...basket,
                    options: {
                        ...basket.options,
                        referrerCode: null,
                    },
                },
                updatedAt: basket.updated_date,
            },
            {
                onError: () => {
                    const errorType = isHosTheme
                        ? ErrorSnackbarActionType.ERROR_CLEAR_REFERRAL_CODE_HOS
                        : ErrorSnackbarActionType.CLOSE_SNACKBAR;

                    dispatchSnackbarError({ type: errorType, payload: null });
                },
                onSuccess: () => setValue(referralCodeDataKeys.referralCode, ''),
            }
        );
    }, [basket, updateBasket, setValue, t]);

    return (
        <Box css={styles.container}>
            <Box css={styles.header}>
                <Typography variant="h2">
                    {isHosTheme ? t('referral_code_title') : t('vita_collective_code_title')}
                </Typography>
                <CustomTooltip
                    title={
                        <Typography variant="body4">
                            {isHosTheme
                                ? t('referral_code_tooltip')
                                : t('vita_collective_code_tooltip')}
                        </Typography>
                    }
                />
            </Box>
            <ControlledField
                name={referralCodeDataKeys.referralCode}
                control={control}
                label={isHosTheme ? t('referral_code_label') : t('vita_collective_code_label')}
                placeholder={
                    isHosTheme
                        ? t('referral_code_placeholder')
                        : t('vita_collective_code_placeholder')
                }
                type="text"
                required={false}
                disabled={!!basket?.options.referrerCode}
                {...(basket?.options.referrerCode && {
                    icon: { icon: <CloseIcon />, onClick: handleClearReferralCode },
                })}
            />
            {!basket?.options.referrerCode && (
                <Button
                    css={styles.button}
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isDisabled}
                    onClick={handleSubmit(onValid)}
                    loading={formState.isValidating || isPending}
                >
                    {t('referral_code_apply_button')}
                </Button>
            )}
            {basket?.options.referrerCode && (
                <Box display="flex" alignItems="center" mt={2} gap={1}>
                    {t('referral_code_applied_message')}
                    <CheckCircleIcon color="success" />
                </Box>
            )}
        </Box>
    );
};
